<template>
  <div
    v-if="visible"
    class="cookie-consent-banner"
    role="region"
    aria-labelledby="cookie-consent-title"
    data-cy="cookie-consent-banner"
  >
    <h2 id="cookie-consent-title" class="cookie-consent-banner__title">We use cookies</h2>
    <p class="cookie-consent-banner__text">
      We use necessary cookies to make {{ brand.name }} work. With your permission, we would also like to use
      Google Analytics cookies to understand how our website and product are used. You can change your choice
      at any time under Cookie settings.
      <a :href="brand.links.cookiePolicy" target="_blank" rel="noopener">Read our cookie policy</a>.
    </p>
    <div class="cookie-consent-banner__actions">
      <v-btn
        class="cookie-consent-banner__button"
        variant="flat"
        color="primary"
        height="40"
        data-cy="cookie-consent-accept"
        @click="decide(true)"
      >
        Accept all
      </v-btn>
      <v-btn
        class="cookie-consent-banner__button"
        variant="flat"
        color="primary"
        height="40"
        data-cy="cookie-consent-reject"
        @click="decide(false)"
      >
        Reject all
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBrand } from '@/composables/useBrand'
import { isEmbedMode } from '@/utils/embed'
import { readConsent, saveConsent, cookieDomainFor, clearAnalyticsCookies } from '@/utils/cookieConsent'

const REJECT_CHANNEL = 'qlerify-cookie-consent'
const REJECT_MESSAGE = 'analytics-rejected'

const brand = useBrand()
const route = useRoute()
const router = useRouter()

const hasAnalytics = Boolean(brand.analytics?.measurementId)
const cookieDomain = cookieDomainFor(window.location.hostname, brand.analytics?.cookieDomain)

const isOpen = ref(hasAnalytics && !readConsent())
// Before the first navigation the route is still "/", which would flash the banner inside embeds.
const routerReady = ref(false)
router.isReady().then(() => {
  routerReady.value = true
})

const visible = computed(() => isOpen.value && routerReady.value && !isEmbedMode(route))

// Also drops _ga cookies left by the loader that ran before consent existed.
const stopAnalytics = () => {
  const wasRunning = Boolean(window.disableGoogleAnalytics?.())
  clearAnalyticsCookies(cookieDomain)
  return wasRunning
}

// Other open tabs may already run gtag.js, so a Reject here has to silence them too.
const consentChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(REJECT_CHANNEL) : null
if (consentChannel) {
  consentChannel.onmessage = (event) => {
    if (event.data === REJECT_MESSAGE) {
      stopAnalytics()
    }
  }
}
onBeforeUnmount(() => consentChannel?.close())

const decide = (analytics) => {
  saveConsent({ analytics, domain: cookieDomain })
  isOpen.value = false

  if (analytics) {
    window.loadGoogleAnalytics?.()
    return
  }

  consentChannel?.postMessage(REJECT_MESSAGE)
  if (stopAnalytics()) {
    window.location.reload()
  }
}

const open = () => {
  isOpen.value = true
}

defineExpose({ open })
</script>

<style scoped>
.cookie-consent-banner {
  position: fixed;
  /* Clears the app footer (about 34px) with the same 16px gap as the right edge. */
  bottom: 50px;
  right: 16px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 420px;
  max-width: calc(100% - 32px);
  padding: 24px;
  background: var(--surface-elevated);
  color: var(--text-dark);
  border: 1px solid var(--light-4);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.08);
}

.cookie-consent-banner__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.cookie-consent-banner__text {
  margin: 0 0 8px;
  line-height: 1.5;
}

.cookie-consent-banner__text a {
  color: inherit;
  text-decoration: underline;
}

.cookie-consent-banner__actions {
  display: flex;
  gap: 12px;
}

.cookie-consent-banner__button {
  flex: 1;
}
</style>
